import { Box, Group, Paper, Text } from '@mantine/core';
import type { ComponentType, ReactNode } from 'react';
import type { WaMessageView } from '../../../lib/whatsapp-message';

/**
 * Shared chrome for the WhatsApp message renderers.
 *
 * Every renderer in `registries/whatsapp-content.tsx` composes from these, so
 * the per-type components stay tiny and can't drift apart visually.
 */

/** Props every renderer receives. `incoming` only ever affects styling —
 *  never *whether* something renders (that was the old bug: several cards
 *  were gated on direction, so an inbound interactive reply and an outbound
 *  location silently collapsed to one line of text). */
export interface WaRendererProps {
  view: WaMessageView;
  incoming: boolean;
}

/** A registry entry. `Body` draws inside the coloured bubble; `Card` draws in
 *  its own tile underneath it, the way WhatsApp renders buttons/attachments. */
export interface WaRenderer {
  Body?: ComponentType<WaRendererProps>;
  Card?: ComponentType<WaRendererProps>;
}

/** Chrome for an "action card" — a plain bordered tile sitting directly below
 *  the coloured bubble (interactive buttons/list, the flow CTA, template
 *  buttons/carousel). One constant so these can't visually drift apart. */
export const ACTION_CARD_STYLE = {
  background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
  borderColor: 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
  color: 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))',
  overflow: 'hidden' as const,
};

/** Action card: full-bleed rows, no padding (the rows supply their own). */
export function ActionCard({ children }: { children: ReactNode }) {
  return (
    <Paper withBorder shadow="xs" radius={14} style={ACTION_CARD_STYLE}>
      {children}
    </Paper>
  );
}

/** Detail card: a padded tile for structured content (location, contacts,
 *  order line items, form answers). */
export function DetailCard({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <Paper
      withBorder
      radius={10}
      p="xs"
      style={{ background: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))' }}
    >
      {title && (
        <Text size="xs" fw={600} c="dimmed" mb={4}>
          {title}
        </Text>
      )}
      {children}
    </Paper>
  );
}

/** One full-width row per button inside an action card — divider between rows,
 *  centered blue accent text, optional leading icon. Shared by the interactive
 *  card, the template-buttons card and each carousel card. */
export function ButtonRows({
  buttons,
}: {
  buttons: Array<{ key: string; icon?: ReactNode; label: string }>;
}) {
  return (
    <>
      {buttons.map((b, i) => (
        <Box
          key={b.key}
          style={{
            padding: '10px 14px',
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--mantine-color-blue-6)',
            borderTop: i === 0 ? 'none' : '1px solid var(--mantine-color-default-border)',
          }}
        >
          {b.icon ? (
            <Group gap={4} justify="center" wrap="nowrap">
              {b.icon}
              <span>{b.label}</span>
            </Group>
          ) : (
            b.label
          )}
        </Box>
      ))}
    </>
  );
}

/** Body text inside a bubble, with WhatsApp's wrapping rules. */
export function BubbleBody({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </Text>
  );
}
